import { useRef, useState, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import Navigation from '../components/Navigation'
import LeagueFooter from '../components/LeagueFooter'
import Lightbox from '@/components/Lightbox'
import { TeamCardSkeleton } from '../components/Skeleton'
import { useIsMobile } from '@/hooks/useIsMobile'
import { getTeamDisplay } from '@/utils/teamDisplay'
import { sf } from '../../../core/features/sf'
import type { HomePageVM } from '../../../core/pages/home/types'
import { ClassicHomeContext } from '../home/context'
import { HomeSection } from '../home/HomeSection'
import { RegistrationSpotlight } from '../home/RegistrationSpotlight'
import { useT } from '@/i18n'
import { siteClassicMessages } from '@/i18n/catalogs/site-classic'
import { siteCoreMessages } from '@/i18n/catalogs/site-core'

// Classic Home view (ARCHITECTURE §6.1 C3). The data and its derivations come
// from the core controller (useHomePage); the markup is today's, verbatim.
// The view keeps only presentation state: the JS breakpoint, the gallery
// lightbox, and the Teams strip's hover and auto-scroll.
export default function Home({ vm }: { vm: HomePageVM }) {
  const c = useT(siteClassicMessages).home
  const nav = useT(siteCoreMessages).pages.nav
  const teams = vm.teams.items
  const galleryImages = vm.data.gallery.images
  const [galleryLightboxIndex, setGalleryLightboxIndex] = useState<number | null>(null)
  const lightbox = useMemo(() => ({ openLightbox: (index: number) => setGalleryLightboxIndex(index) }), [])

  // Detect mobile viewport
  const isMobile = useIsMobile()

  // Refs for scrolling
  const teamsScrollRef = useRef<HTMLDivElement>(null)
  const [isTeamsHovered, setIsTeamsHovered] = useState(false)

  // Auto-scroll teams section
  useEffect(() => {
    if (!teamsScrollRef.current || teams.length === 0 || isTeamsHovered) return

    const container = teamsScrollRef.current
    const checkOverflow = () => {
      return container.scrollWidth > container.clientWidth
    }

    if (!checkOverflow()) return

    let animationFrameId: number
    let lastTimestamp = 0
    const scrollSpeed = 0.5 // pixels per frame (adjust for speed)
    const targetFPS = 60
    const frameInterval = 1000 / targetFPS

    const autoScroll = (timestamp: number) => {
      if (!isTeamsHovered && container) {
        if (timestamp - lastTimestamp >= frameInterval) {
          const maxScroll = container.scrollWidth - container.clientWidth

          if (container.scrollLeft >= maxScroll - 1) {
            // Reached the end, reset to start
            container.scrollLeft = 0
          } else {
            // Continue scrolling
            container.scrollLeft += scrollSpeed
          }

          lastTimestamp = timestamp
        }
        animationFrameId = requestAnimationFrame(autoScroll)
      }
    }

    animationFrameId = requestAnimationFrame(autoScroll)

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId)
      }
    }
  }, [teams, isTeamsHovered])

  return (
    <ClassicHomeContext.Provider value={lightbox}>
    <div className="min-h-screen bg-gradient-to-b from-bg-secondary via-bg-primary to-bg-primary">
      <Navigation noSpacer />
      <h1 className="sr-only">{vm.league.name}</h1>

      {/* 1) HERO + DASHBOARD COMBINATION - Above the Fold */}
      <section id="dashboard" className="relative min-h-screen overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                'radial-gradient(1100px 500px at 70% 10%, rgb(var(--color-secondary) / 0.45), transparent 65%), radial-gradient(900px 500px at 20% 40%, rgb(var(--color-primary) / 0.10), transparent 70%), linear-gradient(180deg, rgb(var(--color-secondary-dark)) 0%, rgb(var(--color-secondary-dark) / 0.6) 45%, rgb(var(--color-bg-primary)) 100%)',
            }}
          />
          <div
            className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage:
                `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
              backgroundSize: '240px 240px',
            }}
          />
        </div>

        {/* Dashboard Overlay */}
        <div className="relative z-20 min-h-screen pt-0 pb-10">
          {/* Banner */}
          <div className="relative w-full h-[38vh] min-h-[280px] sm:h-[44vh] sm:min-h-[340px] lg:h-[46vh] lg:min-h-[420px] lg:max-h-[560px] overflow-hidden" {...sf('home.hero')}>
            <img
              src={vm.league.heroImageUrl}
              alt={c.heroAlt(vm.league.name)}
              className="absolute inset-0 w-full h-full object-cover object-[50%_25%]"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-black/10 to-black/70"></div>
            <div className="absolute inset-0 bg-gradient-to-r from-black/55 via-black/15 to-transparent"></div>
            <div className="absolute inset-x-0 bottom-0 h-24 sm:h-28 lg:h-32 bg-gradient-to-b from-transparent to-bg-primary"></div>
            <div className="absolute -left-32 top-0 h-full w-48 bg-white/10 rotate-12 blur-[1px]"></div>
          </div>

          {/* Dashboard Grid */}
          <div className="container mx-auto px-4 -mt-10 sm:-mt-14 lg:-mt-16">
            <div className="max-w-7xl mx-auto">
              <div className="w-full" style={{ display: 'block' }}>
                <RegistrationSpotlight forms={vm.openForms} />
                {!vm.layout.usedFallback ? (
                  isMobile ? (
                    // Mobile: single column, ordered by mobile_display_order
                    <div className="w-full space-y-4 sm:space-y-5">
                      {vm.layout.mobile.map((slot) => (
                        <div key={slot.key} style={{ height: 'auto' }}>
                          <HomeSection slot={slot} vm={vm} />
                        </div>
                      ))}
                    </div>
                  ) : (
                    // Desktop: two columns
                    <div className="flex flex-col lg:flex-row gap-4 sm:gap-5" style={{ alignItems: 'flex-start', alignContent: 'flex-start' }}>
                      {/* LEFT COLUMN */}
                      {vm.layout.desktop.left.length > 0 && (
                        <div 
                          className="w-full lg:w-[58.33%] space-y-4" 
                          style={{ 
                            flexShrink: 0, 
                            flexGrow: 0,
                            height: 'auto',
                            minHeight: 0,
                            alignSelf: 'flex-start'
                          }}
                        >
                          {vm.layout.desktop.left.map((slot) => (
                            <div key={slot.key} style={{ height: 'auto' }}>
                              <HomeSection slot={slot} vm={vm} />
                            </div>
                          ))}
                        </div>
                      )}

                      {/* RIGHT COLUMN */}
                      {vm.layout.desktop.right.length > 0 && (
                        <div 
                          className="w-full lg:w-[41.67%] space-y-4" 
                          style={{ 
                            flexShrink: 0, 
                            flexGrow: 0,
                            height: 'auto',
                            minHeight: 0,
                            alignSelf: 'flex-start'
                          }}
                        >
                          {vm.layout.desktop.right.map((slot) => (
                            <div key={slot.key} style={{ height: 'auto' }}>
                              <HomeSection slot={slot} vm={vm} />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )
                ) : (
                  // Fallback to default layout if no layout config exists
                  <div className="flex flex-col lg:flex-row gap-4 sm:gap-5" style={{ alignItems: 'flex-start', alignContent: 'flex-start' }}>
                    <div 
                      className="w-full lg:w-[58.33%] space-y-4" 
                      style={{ 
                        flexShrink: 0, 
                        flexGrow: 0,
                        height: 'auto',
                        minHeight: 0,
                        alignSelf: 'flex-start'
                      }}
                    >
                      <HomeSection slot={vm.layout.desktop.left[0]} vm={vm} />
                    </div>
                    <div 
                      className="w-full lg:w-[41.67%] space-y-4" 
                      style={{ 
                        flexShrink: 0, 
                        flexGrow: 0,
                        height: 'auto',
                        minHeight: 0,
                        alignSelf: 'flex-start'
                      }}
                    >
                      <HomeSection slot={vm.layout.desktop.right[0]} vm={vm} />
                    </div>
                  </div>
                )}

                {/* FULL WIDTH SECTIONS (Desktop only - mobile uses mobileSections) */}
                {!isMobile && vm.layout.desktop.full.length > 0 && (
                  <div className="w-full space-y-4 mt-4 sm:mt-5">
                    {vm.layout.desktop.full.map((slot) => (
                      <div key={slot.key}>
                        <HomeSection slot={slot} vm={vm} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2) TEAMS Section */}
      <section id="teams" className="relative py-24 sm:py-32 bg-bg-primary border-t border-secondary/10" {...sf('home.teams')}>
        <div className="container mx-auto px-4">
          <h2 className="text-4xl sm:text-5xl font-black text-white mb-16 uppercase tracking-tight">{nav.teams}</h2>
          <div 
            ref={teamsScrollRef}
            onMouseEnter={() => setIsTeamsHovered(true)}
            onMouseLeave={() => setIsTeamsHovered(false)}
            className="overflow-x-auto pb-6 -mx-4 px-4 scrollbar-hide scroll-smooth"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            <div className="flex gap-10 sm:gap-12 min-w-max" {...(vm.status === 'loading' ? sf('state.loading') : {})}>
              {vm.teams.loading ? (
                <>
                  <TeamCardSkeleton />
                  <TeamCardSkeleton />
                  <TeamCardSkeleton />
                  <TeamCardSkeleton />
                </>
              ) : teams.length > 0 ? (
                teams.map((team) => {
                  const card = (<>
                    <div className="w-40 h-40 sm:w-44 sm:h-44 mx-auto mb-6 rounded-full bg-secondary/5 border border-secondary/20 flex items-center justify-center text-6xl sm:text-7xl group-hover:border-primary group-hover:bg-secondary/10 group-hover:scale-105 transition-all duration-300 overflow-hidden">
                      {team.logoUrl ? (
                        <img src={team.logoUrl || ''} alt={team.name} className="w-full h-full object-cover" />
                      ) : (
                        <span>{team.shortCode?.[0] || team.name[0]}</span>
                      )}
                  </div>
                    <h3 className="text-white font-black text-xl sm:text-2xl group-hover:text-primary transition-colors duration-300">
                      {getTeamDisplay({ name: team.name, short_code: team.shortCode }, 'full')}
                    </h3>
                    {team.city && (
                      <p className="text-white/50 text-sm mt-1">{team.city}</p>
                    )}
                  </>)
                  // #421: link to the team only when the league's Teams section is on.
                  return vm.teams.linkTeams ? (
                    <Link key={team.id} to={team.href} className="flex-shrink-0 w-56 sm:w-64 text-center group cursor-pointer">{card}</Link>
                  ) : (
                    <div key={team.id} className="flex-shrink-0 w-56 sm:w-64 text-center group">{card}</div>
                  )
                })
              ) : (
                <div className="text-white/60 text-center py-8 w-full">{c.noTeams}</div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* 3) COMMUNITY Section — hidden entirely when no quote is set */}
      {vm.league.communityQuote && (
      <section className="relative py-32 sm:py-40 bg-bg-primary border-t border-secondary/20" {...sf('home.community-quote')}>
        <div className="absolute inset-0 opacity-[0.02]">
          <div className="w-full h-full" style={{ backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, var(--color-secondary-hex) 2px, var(--color-secondary-hex) 4px)' }}></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto">
            <div className="h-px bg-gradient-to-r from-transparent via-secondary/30 to-transparent mb-16"></div>
            <div className="text-center">
              <blockquote className="text-4xl sm:text-5xl md:text-6xl font-black text-white leading-[1.15] mb-12 tracking-tight">
                "{vm.league.communityQuote}"
              </blockquote>
              <div className="flex items-center justify-center gap-3">
                <div className="h-px w-16 bg-primary/30"></div>
                <p className="text-primary text-xs uppercase tracking-[0.2em] font-bold">{vm.league.name}</p>
                <div className="h-px w-16 bg-primary/30"></div>
              </div>
            </div>
            <div className="h-px bg-gradient-to-r from-transparent via-secondary/30 to-transparent mt-16"></div>
          </div>
        </div>
      </section>
      )}

      {/* 4) FOOTER */}
      <LeagueFooter />

      {/* Error toast */}
      {vm.error && (
        <div className="fixed bottom-4 right-4 bg-red-900/90 border border-red-500 text-white px-4 py-3 rounded shadow-lg z-50" {...(vm.status === 'error' ? sf('state.error') : {})}>
          <p className="text-sm">{vm.error}</p>
          <button 
            onClick={vm.dismissError}
            {...sf('home.error.dismiss')} 
            className="absolute top-1 right-2 text-white/60 hover:text-white"
          >
            ×
          </button>
        </div>
      )}

      {/* Gallery Lightbox */}
      {galleryLightboxIndex !== null && galleryImages.length > 0 && (
        <Lightbox
          images={galleryImages}
          currentIndex={galleryLightboxIndex}
          onClose={() => setGalleryLightboxIndex(null)}
          onNavigate={setGalleryLightboxIndex}
        />
      )}
    </div>
    </ClassicHomeContext.Provider>
  )
}
